import React, { useState } from 'react';
import I18N from '../I18N';
import Achievement from './Achievement';
import { A, R } from '../routes';

import { T, TraksConsumer } from '../traks';
import { Apify, ApifyParallel, ApifySerialAccumulate } from '../Apify';
import { site_base } from '../Site';

const DividendGameLine = Apify("/z/game_brief")
	.with_query_from_props((props) => ({id: props.game_id}))
	.with_response_prop("game")
	.wrap((props) => {
		const link_to_portfolio = R.href('/portfolio/:id', {id: props.portfolio_id});
		const game_name = props.game.Name;
		const had_quantity = I18N.intval(props.had_quantity);
		const had_stock = props.have_stock_element;
		const received_amount = props.render_received_amount(props.had_quantity, props.game.BankrollCurrency);
		return (
			<span>
				<T>
					Your portfolio in the game <A className="ad_notification_link" href={link_to_portfolio}>{game_name}</A> had {had_quantity} &times; {had_stock} and received {received_amount}.
				</T>
			</span>
		)
	});

const DividendPortfolioLine = Apify("/z/portfolio_brief")
	.with_query_from_props((props) => ({id: props.portfolio_id}))
	.with_response_prop("portfolio")
	.wrap((props) => {
		const link_to_portfolio = R.href('/portfolio/:id', {id: props.portfolio_id});
		const portfolio_name = props.portfolio.Name;
		const had_quantity = I18N.intval(props.had_quantity);
		const had_stock = props.have_stock_element;
		const received_amount = props.render_received_amount(props.had_quantity);
		return (
			<span>
				<T>
					Your portfolio <A className="ad_notification_link" href={link_to_portfolio}>{portfolio_name}</A> had {had_quantity} &times; {had_stock} and received {received_amount}.
				</T>
			</span>
		)
	});

const TradeExecutedNotificationInner = Apify("/a/transaction_info")
	.with_query_from_props((props) => ({transaction_id: props.transaction_id, lang: props.lang}))
	.with_4xx_prop("err")
	.with_response_prop("info")
	.wrap((data) => {
		if (data.err) {
			data.do_regret();
			return <React.Fragment/>;
		}

		let { info } = data;

		let header = <React.Fragment>Transaction</React.Fragment>;
		let body = <React.Fragment>Data is missing</React.Fragment>;

		let up_stock, down_stock;
		for (let m of info.Movements) {
			if (m.Delta > 0) {
				up_stock = m;
			} else {
				down_stock = m;
			}
		}

	if (info.Type === 'short') {
		const stockMove = info.Movements[0];
		const currencyMove = info.Movements.length > 1 ? info.Movements[1] : null;
		const qty = Math.abs(stockMove.Delta);
		const decimals = stockMove.NDecimals || 0;
		const qtyLabel = I18N.floatval(qty, decimals);
		const pricePerShare = stockMove.UnitPrice;
		const currencyTicker = currencyMove ? currencyMove.Ticker : stockMove.Ticker;
		const priceLabel = I18N.floatval_unit(pricePerShare, 2, currencyTicker);
		header = <React.Fragment><T>Your short of</T> <span className="ad_notification_gamelink">{stockMove.Name}</span><T> has gone through</T></React.Fragment>;
		body = (
			<React.Fragment><T>You shorted </T>{qtyLabel} <b>{stockMove.Ticker}</b> <T>at </T>{priceLabel} <T>per share.</T></React.Fragment>
		);
		return (
			<div>
				<h5>{header}</h5>
				<div>
					{body}
				</div>
			</div>
		);
	}

	if (info.Type === 'cover') {
		const stockMove = info.Movements[0];
		const cashMove = info.Movements.find((m) => m.StockId !== stockMove.StockId) || null;
		const qty = stockMove.Delta;
		const decimals = stockMove.NDecimals || 0;
		const qtyLabel = I18N.floatval(qty, decimals);
		const coverPrice = stockMove.UnitPrice;
		const currencyTicker = cashMove ? cashMove.Ticker : stockMove.Ticker;
		const coverPriceLabel = I18N.floatval_unit(coverPrice, 2, currencyTicker);

		let profitValue = null;
		if (cashMove) {
			profitValue = cashMove.Delta;
		} else if (data.pre_trade_asset) {
			const entryPrice = data.pre_trade_asset.ShortUnitPriceInBankrollCurrency;
			if (entryPrice !== undefined && entryPrice !== null) {
				profitValue = (entryPrice - coverPrice) * qty;
			}
		}

		let profitSuffix = null;
		if (profitValue !== null) {
			const profitLabel = I18N.floatval_unit(Math.abs(profitValue), 2, currencyTicker);
			profitSuffix = profitValue >= 0
				? <span className="ad_main_green ms-1"><T> Profit: {profitLabel}</T></span>
				: <span className="ad_main_red ms-1"><T> Loss: {profitLabel}</T></span>;
		}

		header = <React.Fragment><T>Your cover of</T> <span className="ad_notification_gamelink">{stockMove.Name}</span><T> has gone through</T></React.Fragment>;
		body = (
			<div>
				<React.Fragment><T>You covered </T>{qtyLabel} <b>{stockMove.Ticker}</b> <T>at </T>{coverPriceLabel} <T>per share.</T></React.Fragment>
				{profitSuffix}
			</div>
		);
		return (
			<div>
				<h5>{header}</h5>
				<div>
					{body}
				</div>
			</div>
		);
	}

		if (up_stock && down_stock) {
			if (info.Type === "purchase") {
				header = <T>Your purchase of <span className="ad_notification_gamelink">{up_stock.Name}</span> has gone through</T>;
				body = <T>You bought {up_stock.Delta} <b>{up_stock.Ticker}</b> and your bankroll decreased with {I18N.floatval_unit(-down_stock.Delta, 2, down_stock.Ticker)}.</T>;
			} else if (info.Type === "sale") {
				let profit_loss_elem = <T>You had no profit or loss.</T>;
				if(data.pre_trade_asset) {
					const bankroll_increase = parseFloat(up_stock.Delta.toFixed(2));
					const sum_paid_when_bought = parseFloat((-down_stock.Delta * data.pre_trade_asset.PurchaseUnitPriceInBankrollCurrency).toFixed(2));
					if(bankroll_increase > sum_paid_when_bought) {
						profit_loss_elem = <React.Fragment><T>Profit:</T> <span className="ad_main_green">{I18N.floatval_unit(bankroll_increase - sum_paid_when_bought, 2, up_stock.Ticker)}</span></React.Fragment>;
					}
					if(bankroll_increase < sum_paid_when_bought) {
						profit_loss_elem = <React.Fragment><T>Loss:</T> <b><span className="ad_main_red">{I18N.floatval_unit(sum_paid_when_bought - bankroll_increase, 2, up_stock.Ticker)}</span></b></React.Fragment>;
					}
				}
				header = <T>Your sale of <span className="ad_notification_gamelink">{down_stock.Name}</span> has gone through</T>;
				body = <T>You sold {-down_stock.Delta} <b>{down_stock.Ticker}</b> and your bankroll increased with {I18N.floatval_unit(up_stock.Delta, 2, up_stock.Ticker)}.</T>;
				if(data.pre_trade_asset) {
					body = <React.Fragment>{body} {profit_loss_elem}</React.Fragment>;
				}
			}
		}

		return (
			<div>
				<h5>{header}</h5>
				<div>
					{body}
				</div>
			</div>

		);

	});

const TradeExecutedNotification = (props) => <TraksConsumer><TradeExecutedNotificationInner {...props}/></TraksConsumer>

const renderers = {
	GameStart(data) {
		const game_link = R.href('/game/:id', {id: data.GameId});
		const name = data.GameName;
		return (
			<div>
				<div className="ad_notification_header"><T>The game <A className="ad_notification_gamelink" href={game_link}>{name}</A> has started. Good luck!</T></div>
			</div>
		)
	},

	GameEnd(data) {
		const game_link = R.href('/game/:id', {id: data.GameId});
		const player_count = data.NumberOfPlayers;
		const ranking = data.Place;
		const portfolio_name = data.PortfolioName;
		const final_value = I18N.floatval(data.FinalValue, 2);
		return (
			<div>
				<div className="ad_notification_header"><T>Game <A className="ad_notification_gamelink" href={game_link}>{portfolio_name}</A> has ended</T></div>
				<div><T>You ranked no. {ranking} of {player_count} players</T></div>
				<div><T>The final value was {final_value} kr.</T></div>
			</div>
		);
	},

	TradeExecuted(data) {
		let pre_trade_asset = null;
		if (data.PreTradeAsset) {
			pre_trade_asset = data.PreTradeAsset;
		}
		return <TradeExecutedNotification transaction_id={data.TransactionId} pre_trade_asset={pre_trade_asset} do_regret={data.do_regret}/>
	},

	TradeAborted(data) {
		let reason;
		switch (data.Reason) {
		case "StockDelisted":
			reason = <div>Stock was delisted</div>;
			break;
		case "StockBanned":
			reason = <div>Stock was banned</div>;
			break;
		default:
			reason = <div>Reason is unknown</div>;
			break;
		}

		// XXX TODO also render trade request?

		return (
			<div>
				<div className="ad_notification_header">Enqueued trade was aborted</div>
				{reason}
			</div>
		);
	},

	// XXX legacy support: "QueuedTradeExecuted" is no longer sent, but may
	// be found in old notifications
	QueuedTradeExecuted(data) {
		/* data.Request (TradeRequest)
		   data.Response (TradeResponse) */
		const x = data.Response.Executed;

		var header;
		var body;
		var money = x.BankrollDelta;
		var stock_delta = x.StockQuantityDelta;

		if (money < 0) {
			money = money * -1;
		}

		if(stock_delta < 0) {
			stock_delta = stock_delta * -1;
		}

		if(x.StockQuantityDelta < 0) {
			header = <T>Your sale of <span className="ad_notification_gamelink">{x.StockName}</span> has gone through</T>;
			body = <T>You sold {stock_delta} <b>{x.Ticker}</b> and your bankroll increased with {I18N.floatval_unit(money, 2, x.BankrollCurrency)}.</T>;
		} else {
			header = <T>Your purchase of <span className="ad_notification_gamelink">{x.StockName}</span> has gone through</T>;
			body = <T>You bought {stock_delta} <b>{x.Ticker}</b> and your bankroll decreased with {I18N.floatval_unit(money, 2, x.BankrollCurrency)}.</T>;
		}

		return (
			<div>
				<div className="ad_notification_header">{header}</div>
				{body}
			</div>
		);

	},

	// XXX legacy support: "QueuedTradeAborted" is no longer sent, but may
	// be found in old notifications
	QueuedTradeAborted(data) {
		let render_trade_request = function(rq) {
			// TODO improve
			if (!rq) {
				return null // XXX sometimes request is missing...
			} else if (rq.BuyWithAmount > 0) {
				return <div><T>Buy {rq.Ticker} for {rq.BuyWithAmount}</T></div>;
			} else if (rq.SellQuantity > 0) {
				return <div><T>Sell {rq.SellQuantity} {rq.Ticker}</T></div>;
			} else {
				throw new Error("invalid rq");
			}
		}

		var reason;
		switch (data.Reason) {
		case "InsufficientQuantity":
			reason = <div>Insufficient quantity/amount at time of trade</div>;
			break;
		case "StockDelisted":
			reason = <div>Stock was delisted</div>;
			break;
		case "StockHalted":
			reason = <div>Stock trade was halted</div>;
			break;
		case "StockBanned":
			reason = <div>Stock was banned</div>;
			break;
		case "GameHasEnded":
			reason = <div>The game has ended</div>;
			break;
		case "Invalid":
		case "InternalError":
		default:
			reason = <div>Reason is unknown</div>;
			break;
		}

		return (
			<div>
				<div className="ad_notification_header">Enqueued trade was aborted</div>
				{reason}
				{render_trade_request(data.Request)}
			</div>
		);
	},

	InvitationAccepted(data) {
		const game_href = R.href('/game/:id', {id: data.GameId});
		return (
			<div>
				<div className="ad_notification_header"><T>{data.UserName} joined <A href={game_href} className="ad_notification_gamelink">{data.GameName}</A></T></div>
			</div>
		);
	},

	StockSplit: ApifyParallel(

		Apify("/z/split")
			.with_query_from_props((props) => ({id: props.SplitId}))
			.with_4xx_prop("split_error")
			.with_response_prop("split"),

		Apify("/z/game_info")
			.with_predicate(props => !!props.GameId)
			.with_prop_comparator((a,b) => a.GameId === b.GameId)
			.with_query_from_props((props) => ({game_id: props.GameId, portfolio_id: props.PortfolioId}))
			.with_response_prop("game")

	).wrap((data) => {
		if (data.split_error) {
			return <div>No split info</div>;
		}
		const stock_href = R.href('/ticker/:exchange/:ticker', {exchange: data.split.Exchange, ticker: data.split.Ticker});
		const stock = data.split.Name;

		var body = null;

		const numerator = data.split.Numerator;
		const denominator = data.split.Denominator;

		if (data.game) {
			const game_href = R.href('/game/:id', {id: data.GameId});
			const game_name = data.game.Name;
			const quantity_before = I18N.floatval(data.QuantityBefore, data.split.NDecimals);
			const quantity_after = I18N.floatval(data.QuantityAfter, data.split.NDecimals);
			const bankroll_delta = I18N.floatval_unit(data.BankrollDelta, 2, data.game.BankrollCurrency);

			let liquidation_text = null;
			if(data.BankrollDelta > 0) {
				liquidation_text = <li><T>Liquidation added {bankroll_delta} to your bankroll.</T></li>;
			}

			var after = null;
			if (data.QuantityAfter) {
				after = <li><T>Quantity after split: {quantity_after}</T></li>;
			} else {
				after = <li><T>Asset was liquidated due to insufficient amount for split.</T></li>;
			}

			body = (
				<div>
					<T>Your assets were automatically adjusted in the game <A href={game_href}>{game_name}</A></T>:
					<ul>
						<li><T>Split ratio: {numerator} for {denominator}</T></li>
						<li><T>Quantity before split: {quantity_before}</T></li>
						{after}
						{liquidation_text}
					</ul>
				</div>
			);
		} else {
			const portfolio_href = R.href('/portfolio/:id', {id: data.PortfolioId});
			body = (
				<div>
					<T>{stock} underwent a stock split with ratio {numerator} for {denominator}.</T>
					<T><A href={portfolio_href}>One of your portfolios</A> contains this stock, and no action was taken.</T>
					<T>You can adjust the quantity yourself, if you want to compensate.</T>
				</div>
			);
		}

		return (
			<div>
				<div className="ad_notification_header"><T><A href={stock_href}>{stock} had a stock split</A></T></div>
				{body}
			</div>
		);
	}),

	ForceSell(data) {
		const Inner = (data) => {
			const assets = data.Assets.map((a) => {
				const quantity = I18N.floatval(a.Quantity, a.NDecimals);
				const stock = a.Stock + " (" + a.Exchange + ":" + a.Ticker + ")";
				const amount = I18N.floatval_unit(a.BankrollDelta, 2, a.BankrollCurrency);
				return (
					<li key={a.Exchange+":"+a.Ticker}>
						<T>{quantity} &times; {stock} sold for {amount}</T>
					</li>
				);
			});

			return (
				<div>
					<div className="ad_notification_header"><T>Automatic sale of assets due to delisting</T></div>
					<T>
					The following assets have been sold
					because they are now delisted on
					Aktiedysten.
					</T>
					<ul>{assets}</ul>
				</div>
			);
		}
		return <TraksConsumer><Inner {...data}/></TraksConsumer>;
	},

	Dividend: ApifySerialAccumulate(
		Apify("/z/dividend")
			.with_query_from_props((props) => ({id: props.DividendId}))
			.with_response_prop("dividend"),
		ApifyParallel(
			Apify("/z/stock")
				.with_query_from_props((props) => ({id: props.dividend.HaveStockId}))
				.with_response_prop("have_stock"),

			Apify("/z/stock")
				.with_query_from_props((props) => ({id: props.dividend.GiveStockId}))
				.with_response_prop("give_stock"),
		)
	).wrap((props) => {
		const { dividend, have_stock, give_stock } = props;

		const have_stock_element = (
			<A className="ad_notification_link" href={R.href('/ticker/:exchange/:ticker', {exchange: have_stock.Exchange, ticker: have_stock.Ticker})}>
				{have_stock.Name}
			</A>
		);

		const render_details = (render_amount) => {
			const render_portfolio = (p) => {
				const render_received_amount = (had_quantity, bankroll_currency) => {
					return render_amount(had_quantity*dividend.Amount*p.ExchangeRate, bankroll_currency);
				};

				if (p.GameId > 0) {
					return (
						<DividendGameLine
							game_id={p.GameId}
							had_quantity={p.HadQuantity}
							portfolio_id={p.PortfolioId}
							have_stock_element={have_stock_element}
							render_received_amount={render_received_amount}
						/>
					);
				} else {
					return (
						<DividendPortfolioLine
							portfolio_id={p.PortfolioId}
							had_quantity={p.HadQuantity}
							have_stock_element={have_stock_element}
							render_received_amount={render_received_amount}
						/>
					);
				}
			}

			if (props.Portfolios.length >= 2) {
				const list_elements = props.Portfolios.map((p) => {
					return <li key={p.PortfolioId}>{render_portfolio(p)}</li>;
				});
				return <ul>{list_elements}</ul>;
			} else {
				return render_portfolio(props.Portfolios[0]);
			}
		};

		const on_date = I18N.date(dividend.PaymentDate);

		var amount, render_amount, give_stock_element;
		switch (props.dividend.Type) {
		case "company_split":
			amount = I18N.intval(dividend.Amount);
			render_amount = (amount) => {
				const amount_element = I18N.intval(amount);
				const name = give_stock.Name;
				return <span><T>{amount_element} shares of {name}</T></span>
			};
			give_stock_element = (
				<A className="ad_notification_link" href={R.href('/ticker/:exchange/:ticker', {exchange: give_stock.Exchange, ticker: give_stock.Ticker})}>
					{give_stock.Name}
				</A>
			);
			return (
				<div>
					<div className="ad_notification_header"><T>Company split</T></div>
					<span><T>{have_stock_element} split on {on_date} into {amount} &times; {give_stock_element} per share.</T></span>
					<React.Fragment> </React.Fragment>
					<span>{render_details(render_amount)}</span>
				</div>
			);
		case "gift_from_god":
			const our_own_email = "info@" + site_base;

			amount = I18N.intval(dividend.Amount);
			render_amount = (amount) => {
				const amount_element = I18N.intval(amount);
				const name = give_stock.Name;
				return <span><T>{amount_element} shares of {name}</T></span>
			};
			give_stock_element = (
				<A className="ad_notification_link" href={R.href('/ticker/:exchange/:ticker', {exchange: give_stock.Exchange, ticker: give_stock.Ticker})}>
					{give_stock.Name}
				</A>
			);
			return (
				<div>
					<div className="ad_notification_header"><T>Game admin addition</T></div>
					<span><T>Game admin awarded {amount} &times; {give_stock_element} with effective date {on_date}. If you want to know why, please reach out to {our_own_email}.</T></span>
					<React.Fragment> </React.Fragment>
					<span>{render_details(render_amount)}</span>
				</div>
			);
		case "dividend_income":
		default:
			amount = I18N.floatval_unit(dividend.Amount, 2, give_stock.Ticker);
			render_amount = (amount, bankroll_currency) => I18N.floatval_unit(amount, 2, bankroll_currency || give_stock.Ticker);
			return (
				<div>
					<div className="ad_notification_header"><T>Dividend income</T></div>
					<span><T>{have_stock_element} paid dividends on {on_date} and gave {amount} per share.</T></span>
					<React.Fragment> </React.Fragment>
					<span>{render_details(render_amount)}</span>
				</div>
			);
		}
	}),

	LateSplitLostMoney(data) {
		const date = I18N.date(data.ExDate);
		return (
			<div>
				<div className="ad_notification_header">Korrektion af værdier</div>
				<p>
				Kære spiller. Du vil bemærke at du har mistet
				en større sum penge. Det skyldes at pengene
				blev hentet ved et omvendt aktiesplit {date},
				som man ikke kan se som en kursstigning, da man
				jo skal have reduceret sin beholdning
				tilsvarende. Vi ville virkelig ønske vi var
				bedre til at fange disse reverse splits i tide,
				men denne her smuttede desværre igennem nettet.
				Og det beklager vi dybt! Du er selvfølgelig
				velkommen til at skrive til os på <a href="mailto:aktiedysten@aktiedysten.dk">aktiedysten@aktiedysten.dk</a> eller
				ringe på +45 53 53 97 91 hvis du har
				nogle spørgsmål, m.m.
				</p>
				<p>
				God dag!
				</p>
			</div>
		);
	},

	PolymorphStock: ApifyParallel(
		Apify("/z/stock")
			.with_query_from_props((props) => ({id: props.OldStockId}))
			.with_response_prop("old_stock"),
		Apify("/z/stock")
			.with_query_from_props((props) => ({id: props.NewStockId}))
			.with_response_prop("new_stock"),
	).wrap((data) => {
		const { old_stock, new_stock } = data;
		const old_thing = <span title={old_stock.Ticker}>{old_stock.Name}</span>;
		const new_thing = <A className="ad_notification_link" href={R.href('/ticker/:exchange/:ticker', {exchange: new_stock.Exchange, ticker: new_stock.Ticker})}>{new_stock.Name}</A>;
		return (
			<div>
				<div className="ad_notification_header"><T>Conversion of equities</T></div>
				<p>
				<T>
				Dear user,
				Your {old_thing} equities has been converted to {new_thing}, that has replaced it in the game. Sincerely, Aktiedysten.
				</T>
				</p>
			</div>
		);
	}),

	GameUserBan: Apify("/a/game_user_ban")
		.with_query_from_props((props) => ({id: props.BanId}))
		.with_response_prop("ban")
	.wrap((props) => {
		return (
			<div>
				<div className="ad_notification_header"><T>Banned from game</T>: {props.ban.GameName}</div>
				<span>{props.ban.Reason}</span>
			</div>
		);
	}),

	Message(data) {
		const Inner = (props) => {
			let msg = data[props.lang];
			if (!msg) msg = data.en;
			return (
				<div>
					<div className="ad_notification_header">{msg.Title}</div>
					<p>
					{msg.Body}
					</p>
				</div>
			);
		};
		return <TraksConsumer><Inner {...data}/></TraksConsumer>;
	},
}

function Notification(props) {
	const n = props.notification;
	const ts = I18N.datetime_short(new Date(n.Ts*1000));
	const type = n.Type;
	const data = n.Data;
	const achievements = n.Achievements;

	const [ regret, set_regret ] = useState(false);

	if (regret) return <React.Fragment/>;

	const do_regret = () => set_regret(true);

	var body = null;

	if (renderers[type]) {
		const Renderer = renderers[type];
		const d = {...data, do_regret};
		body = <Renderer {...d}/>;
	} else if (typeof data === "string") {
		body = <span>{data}</span>;
	} else {
		body = <span>{JSON.stringify(data)}</span>;
	}

	var achievements_element;
	if (achievements && achievements.length > 0) {
		const achievements_list = achievements.map((a) => <Achievement key={a} id={a}/>);
		achievements_element = (
			<div className="ad_notification_body">
				<div className="ad_notification_header"><T>Achievements won:</T></div>
				{achievements_list}
			</div>
		);
	}

	return (
		<div className="ad_notification_box py-3 px-4 mb-3">
			<div className="ad_notification_body">{body}</div>
			{achievements_element}
			<div className="ad_notification_date">{ts}</div>
		</div>
	);
}

export default Notification;



// WEBPACK FOOTER //
// ./src/c/Notification.js